import { useState } from 'react';
import { User } from '../types';
import { mockUsers } from '../mockData';
import ConfirmModal from './ConfirmModal';

interface UserTableProps {
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  onViewDetail: (user: User) => void;
}

export default function UserTable({ selectedUsers, setSelectedUsers, onViewDetail }: UserTableProps) {
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; type: 'enable' | 'disable' | 'toggle'; user?: User }>({
    open: false,
    type: 'enable',
  });
  const [editingRemark, setEditingRemark] = useState<{ uid: string; value: string } | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users.map((u) => u.uid));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (uid: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, uid]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== uid));
    }
  };

  const handleBatchOperation = (type: 'enable' | 'disable') => {
    if (selectedUsers.length === 0) {
      alert('请先选择用户');
      return;
    }
    setConfirmModal({ open: true, type });
  };

  const handleToggleStatus = (user: User) => {
    setConfirmModal({ open: true, type: 'toggle', user });
  };

  const handleConfirm = () => {
    const { type, user } = confirmModal;
    
    if (type === 'toggle' && user) {
      setUsers(users.map((u) => 
        u.uid === user.uid ? { ...u, status: u.status === 'normal' ? 'disabled' : 'normal' } : u
      ));
    } else if (type === 'enable' || type === 'disable') {
      const newStatus = type === 'enable' ? 'normal' : 'disabled';
      setUsers(users.map((u) => 
        selectedUsers.includes(u.uid) ? { ...u, status: newStatus } : u
      ));
      setSelectedUsers([]);
    }
    
    setConfirmModal({ open: false, type: 'enable' });
  };

  const handleSaveRemark = (uid: string, value: string) => {
    setUsers(users.map((u) => (u.uid === uid ? { ...u, remark: value } : u)));
    setEditingRemark(null);
  };

  const maskPhone = (phone: string) => {
    return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            已选择 <span className="text-teal-600 font-medium">{selectedUsers.length}</span> 项
          </span>
          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBatchOperation('enable')}
                className="px-4 py-1.5 bg-teal-50 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors whitespace-nowrap"
              >
                批量启用
              </button>
              <button
                onClick={() => handleBatchOperation('disable')}
                className="px-4 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
              >
                批量禁用
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">UID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">头像</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">用户名</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">手机号码</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">注册时间</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">最近登录</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">实名状态</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">账号状态</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">余额</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">积分</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">租户</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">备注</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.uid)}
                    onChange={(e) => handleSelectUser(user.uid, e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{user.uid}</td>
                <td className="px-4 py-3">
                  <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{user.username}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{maskPhone(user.phone)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{user.registerTime}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{user.lastLoginTime}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.isVerified ? '已实名' : '未实名'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={user.status === 'normal'}
                      onChange={() => handleToggleStatus(user)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                  </label>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">¥{user.balance.toFixed(2)}</td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{user.points}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {user.tenants.length > 0 ? user.tenants.join(', ') : '-'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {editingRemark?.uid === user.uid ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={editingRemark.value}
                        onChange={(e) => setEditingRemark({ uid: user.uid, value: e.target.value })}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveRemark(user.uid, editingRemark.value)}
                        className="w-6 h-6 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded cursor-pointer"
                      >
                        <i className="ri-check-line text-base"></i>
                      </button>
                      <button
                        onClick={() => setEditingRemark(null)}
                        className="w-6 h-6 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <i className="ri-close-line text-base"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-600">{user.remark || '-'}</span>
                      <button
                        onClick={() => setEditingRemark({ uid: user.uid, value: user.remark })}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => onViewDetail(user)}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看详情
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          共 {users.length} 条记录
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
            上一页
          </button>
          <button className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors whitespace-nowrap">
            1
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
            2
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
            3
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap">
            下一页
          </button>
        </div>
      </div>

      {confirmModal.open && (
        <ConfirmModal
          type={confirmModal.type}
          user={confirmModal.user}
          count={selectedUsers.length}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmModal({ open: false, type: 'enable' })}
        />
      )}
    </div>
  );
}
